import React, { useState, useEffect } from 'react';
import { dressStatusService, listingsService, branchService, showToast } from '../services';

const ListingWizard = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [dressStatuses, setDressStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardData, setWizardData] = useState({
    selectedStatus: null,
    listingId: null,
    notes: '',
    action: 'update',
    branchId: null
  });
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const totalSteps = 3;

  useEffect(() => {
    if (isOpen) {
      fetchDressStatuses();
    }
  }, [isOpen]);

  // Load branches when moving to confirmation step and status requires branch selection
  useEffect(() => {
    const needsBranch = wizardData.selectedStatus && (
      wizardData.selectedStatus.slug === 'listed' ||
      wizardData.selectedStatus.name?.toLowerCase() === 'listed'
    );
    if (isOpen && currentStep === 3 && needsBranch && branches.length === 0) {
      fetchBranches();
    }
  }, [isOpen, currentStep, wizardData.selectedStatus]);

  const fetchDressStatuses = async () => {
    try {
      setLoading(true);
      const response = await dressStatusService.getAll();
      if (response.success) {
        setDressStatuses(response.data);
      } else {
        showToast.error('Failed to fetch dress statuses');
      }
    } catch (error) {
      console.error('Error fetching dress statuses:', error);
      showToast.error('Error fetching dress statuses');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      setBranchesLoading(true);
      // Fetch many to avoid pagination misses
      const response = await branchService.getAll({ per_page: 1000 });
      if (response?.data) {
        // If paginated, normalize to response.data.data
        const list = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setBranches(list);
      } else if (Array.isArray(response)) {
        setBranches(response);
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      showToast.error('Failed to load branches');
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(wizardData);
    onClose();
  };

  const updateWizardData = (updates) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1StatusSelection
            dressStatuses={dressStatuses}
            selectedStatus={wizardData.selectedStatus}
            onStatusSelect={(status) => updateWizardData({ selectedStatus: status })}
            loading={loading}
          />
        );
      case 2:
        return (
          <Step2ListingSelection
            selectedStatus={wizardData.selectedStatus}
            listingId={wizardData.listingId}
            onListingSelect={(listingId) => updateWizardData({ listingId })}
          />
        );
      case 3:
        return (
          <Step3ActionConfirmation
            wizardData={wizardData}
            dressStatuses={dressStatuses}
            onDataUpdate={updateWizardData}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Listing Management Wizard
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center">
            {Array.from({ length: totalSteps }, (_, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index + 1 <= currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index + 1}
                </div>
                {index < totalSteps - 1 && (
                  <div
                    className={`w-16 h-1 mx-2 ${
                      index + 1 < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <span className="text-sm text-gray-600">
              Step {currentStep} of {totalSteps}: {
                currentStep === 1 ? 'Select Status' :
                currentStep === 2 ? 'Select Listing' :
                'Confirm Action'
              }
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-96">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              currentStep === 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            
            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  canProceed()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={!canComplete()}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  canComplete()
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Complete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function canProceed() {
    switch (currentStep) {
      case 1:
        return wizardData.selectedStatus !== null;
      case 2:
        return wizardData.listingId !== null;
      default:
        return true;
    }
  }

  function canComplete() {
    const hasBasics = wizardData.selectedStatus && wizardData.listingId;
    if (!hasBasics) return false;
    // If marking as Listed, require a branch selection
    const isListed = wizardData.selectedStatus && (
      wizardData.selectedStatus.slug === 'listed' ||
      wizardData.selectedStatus.name?.toLowerCase() === 'listed'
    );
    if (isListed) {
      return Boolean(wizardData.branchId);
    }
    return true;
  }
};

// Step 1: Status Selection
const Step1StatusSelection = ({ dressStatuses, selectedStatus, onStatusSelect, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading dress statuses...</span>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Select Dress Status
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Choose the status you want to apply to listings or filter by.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {dressStatuses.map((status) => (
          <div
            key={status.id}
            onClick={() => onStatusSelect(status)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
              selectedStatus?.id === status.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded-full mr-3"
                style={{ backgroundColor: status.color }}
              ></div>
              <div>
                <h4 className="font-medium text-gray-900">{status.name}</h4>
                <p className="text-sm text-gray-600">{status.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Step 2: Listing Selection
const Step2ListingSelection = ({ selectedStatus, listingId, onListingSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedStatus) {
      fetchListings();
    }
  }, [selectedStatus, searchTerm]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const response = await listingsService.getAll({ 
        search: searchTerm,
        per_page: 50 // Get more listings for selection
      });
      
      if (response.success) {
        setListings(response.data.data || []);
      } else {
        showToast.error('Failed to fetch listings');
        setListings([]);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      showToast.error('Error fetching listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Select Listing
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Choose a listing to update its status to <strong>{selectedStatus?.name}</strong>. 
        Showing all listings for selection.
      </p>
      
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search listings by ID or username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Listings List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading listings...</span>
          </div>
        ) : (
          listings.map((listing) => (
            <div
              key={listing.id}
              onClick={() => onListingSelect(listing.listing_id)}
              className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                listingId === listing.listing_id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-1">
                    <span className="font-medium text-gray-900">{listing.listing_id}</span>
                    <span className="text-gray-600 ml-2">by {listing.username}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Current Status:</span>
                    <span 
                      className="px-2 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: listing.status_color || '#6B7280' }}
                    >
                      {listing.status_name || 'Unknown'}
                    </span>
                  </div>
                </div>
                <span className="text-sm text-gray-500 ml-4">{listing.created_date}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Step 3: Action Confirmation
const Step3ActionConfirmation = ({ wizardData, dressStatuses, onDataUpdate }) => {
  const selectedStatus = dressStatuses.find(s => s.id === wizardData.selectedStatus?.id);
  const requiresBranch = selectedStatus && (
    selectedStatus.slug === 'listed' || selectedStatus.name?.toLowerCase() === 'listed'
  );

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Confirm Action
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Review your selection before completing the action.
      </p>
      
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selected Status
            </label>
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded-full mr-2"
                style={{ backgroundColor: selectedStatus?.color }}
              ></div>
              <span className="text-gray-900">{selectedStatus?.name}</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Listing ID
            </label>
            <span className="text-gray-900">{wizardData.listingId}</span>
          </div>
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Notes (Optional)
          </label>
          <textarea
            value={wizardData.notes}
            onChange={(e) => onDataUpdate({ notes: e.target.value })}
            placeholder="Add any notes about this status change..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      {requiresBranch && (
        <div className="bg-amber-50 p-4 rounded-lg mb-6 border border-amber-200">
          <label className="block text-sm font-medium text-amber-900 mb-2">
            Assign Store/Branch (required for Listed)
          </label>
          <BranchSelector
            value={wizardData.branchId}
            onChange={(branchId) => onDataUpdate({ branchId })}
          />
          {!wizardData.branchId && (
            <div className="text-sm text-amber-700 mt-2">
              Please select a branch to list the item at.
            </div>
          )}
        </div>
      )}

      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Action Summary</h4>
        <p className="text-sm text-blue-800">
          This will update listing <strong>{wizardData.listingId}</strong> to status{' '}
          <strong>{selectedStatus?.name}</strong>
          {wizardData.notes && (
            <>
              {' '}with the note: <em>"{wizardData.notes}"</em>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

// Lightweight branch selector (local to this file to avoid broader style impact)
const BranchSelector = ({ value, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await branchService.getAll({ per_page: 1000 });
        const list = res?.data ? (Array.isArray(res.data) ? res.data : (res.data.data || [])) : [];
        if (mounted) setItems(list);
      } catch (e) {
        console.error('BranchSelector error:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className="form-select"
      style={{ width: '100%', height: '40px' }}
      disabled={loading}
    >
      <option value="">{loading ? 'Loading branches...' : 'Select a branch'}</option>
      {items.map((b) => (
        <option key={b.id} value={b.id}>
          {b.branch_name || b.name || `Branch #${b.id}`}
        </option>
      ))}
    </select>
  );
};

export default ListingWizard;
